import { AudioFormService } from '@/features/audio-form/data/audio-form.service.ts'
import type {
  AudioFormInput,
  StsScriptAsset,
  TtsScriptAsset,
  VoiceAsset,
  VoicePreset,
} from '@/features/audio-form/data/dto/audio-form-input.ts'
import { fetchAudioFormUntilProcessed } from '@/features/audio-form/domain/use-cases/generate-audioform.ts'
import type { Speech } from '@/features/speech/domain/models/speech.ts'

const VOICE_ASSET_REF = 'voice0'
const TTS_ASSET_REF = 'tts0'
const MEDIA_ASSET_REF = 'media0'
const STS_ASSET_REF = 'sts0'

const toVoicePreset = (expressive: boolean): VoicePreset =>
  expressive ? 'expressive' : 'standard'

const createBaseAudioform = (
  assets: AudioFormInput['assets'],
  speechAssetRef: string
): AudioFormInput => ({
  header: {
    version: '2',
  },
  assets,
  production: {
    arrangement: {
      sections: [
        {
          layers: [{ clips: [{ assetRef: speechAssetRef, marginStart: 0 }] }],
        },
      ],
    },
    masteringPreset: 'balanced',
  },
  delivery: {
    loudnessPreset: 'spotify',
    encoderPreset: 'mp3',
    public: false,
  },
})

const createVoiceAsset = (
  voiceAlias: string,
  expressive: boolean,
  speed?: number
): VoiceAsset => ({
  type: 'voice',
  voiceAlias,
  ...(speed === undefined ? {} : { speed }),
  voicePreset: toVoicePreset(expressive),
})

const createTtsAudioform = (
  scriptText: string,
  voiceAlias: string,
  speed: number,
  expressive: boolean
): AudioFormInput =>
  createBaseAudioform(
    {
      [VOICE_ASSET_REF]: createVoiceAsset(voiceAlias, expressive, speed),
      [TTS_ASSET_REF]: {
        type: 'tts',
        text: scriptText,
        voiceRef: VOICE_ASSET_REF,
      } satisfies TtsScriptAsset,
    },
    TTS_ASSET_REF
  )

const createStsAudioform = (
  fileId: string,
  voiceAlias: string,
  expressive: boolean
): AudioFormInput =>
  createBaseAudioform(
    {
      [VOICE_ASSET_REF]: createVoiceAsset(voiceAlias, expressive, 1),
      [MEDIA_ASSET_REF]: {
        type: 'media',
        fileId,
      },
      [STS_ASSET_REF]: {
        type: 'sts',
        mediaRef: MEDIA_ASSET_REF,
        voiceRef: VOICE_ASSET_REF,
      } satisfies StsScriptAsset,
    },
    STS_ASSET_REF
  )

const createSpeechAudioform = async (
  audioform: AudioFormInput
): Promise<{ audioformId: string; result: AudioFormInput }> => {
  const audioformId = await AudioFormService.createAudioFormVersion(audioform)
  const result = await fetchAudioFormUntilProcessed(audioformId)
  return { audioformId, result }
}

const getSpeechUrl = (result: AudioFormInput): string => {
  if (!result.delivery.uri) {
    throw new Error('Audioform delivery URI is missing')
  }

  return result.delivery.uri
}

export const SpeechService = {
  createSpeech: async (
    scriptText: string,
    voiceAlias: string,
    speed = 1,
    expressive = false
  ): Promise<Speech> => {
    const { audioformId, result } = await createSpeechAudioform(
      createTtsAudioform(scriptText, voiceAlias, speed, expressive)
    )
    const ttsAsset = result.assets[TTS_ASSET_REF]
    const anchor = ttsAsset?.type === 'tts' ? ttsAsset.anchor : undefined

    return {
      speechId: anchor ?? audioformId,
      speechUrl: getSpeechUrl(result),
      anchor,
      type: 'tts',
    }
  },

  createSpeechToSpeech: async (
    fileId: string,
    voiceAlias: string,
    expressive = false
  ): Promise<Speech> => {
    const { audioformId, result } = await createSpeechAudioform(
      createStsAudioform(fileId, voiceAlias, expressive)
    )

    return {
      speechId: audioformId,
      speechUrl: getSpeechUrl(result),
      fileId,
      type: 'sts',
    }
  },
}
